import { GENERAL_ID } from '../components/Stores/exposureDataStore';
import { IMaterialExposure, INetExposure } from '../sp-types/netExposureType';
import { convertDateToMonthYear } from './MonthYear/monthYear';
import { getAllInventoryReportOfAccount } from '../sp-firebase/inventoryReportFB';
import { getPositions } from '../sp-firebase/financialPositionsFB';
import { FutureOrderExposure } from '../sp-types/FutureOrderType';
import { IInventoryReportType } from '../sp-types/InventoryReportType';
import { IFinancialPosition } from '../sp-types/financialPositionType';
import { getFutureExposure } from '../sp-firebase/futureOrderExposureFB';
import { Timestamp } from 'firebase/firestore';

export const getGeneralExposure = (
  exposure: INetExposure,
  convertionMap: Record<string, number>,
): IMaterialExposure => {
  const initialAccumulator: IMaterialExposure = {
    financialPositions: 0,
    futureOrder: 0,
    inventorySum: 0,
    futureExpCalendar: [],
    positionExpCalendar: [],
  };

  return Object.keys(exposure.materialsExposure)
    .filter((key) => key !== GENERAL_ID)
    .reduce((acc, materialId) => {
      const currentObject = exposure.materialsExposure[materialId] || {
        financialPositions: 0,
        futureOrder: 0,
        inventorySum: 0,
      };

      acc.financialPositions += currentObject.financialPositions * convertionMap[materialId];
      acc.futureOrder += currentObject.futureOrder * convertionMap[materialId];
      acc.inventorySum += currentObject.inventorySum * convertionMap[materialId];

      return acc;
    }, initialAccumulator);
};

export const getExposureCalc = ({ inventorySum, financialPositions, futureOrder }: IMaterialExposure): number =>
  inventorySum + financialPositions - futureOrder;

export const getNetExposure = (
  exposure: INetExposure,
  convertionMap: Record<string, number>,
  materialId: string,
  isTotal?: boolean,
): number => {
  const materialExposure = isTotal
    ? getGeneralExposure(exposure, convertionMap)
    : exposure.materialsExposure[materialId];
  return materialExposure ? getExposureCalc(materialExposure) : 0;
};

export const adjustExposureByMaterialPrices = (
  netExposure: INetExposure,
  materialPriceMap: Record<string, number>,
): INetExposure => {
  const adjustedMaterialsExposure: Record<string, IMaterialExposure> = Object.entries(
    netExposure.materialsExposure,
  ).reduce((acc, [materialId, { positionExpCalendar, futureExpCalendar, inventorySum, ...exposure }]) => {
    const price = materialPriceMap[materialId] || 1;
    acc[materialId] = {
      inventorySum: inventorySum * price,
      inventoryCalendar: { value: inventorySum, date: convertDateToMonthYear(new Date()) },
      futureOrder: exposure.futureOrder * price,
      financialPositions: exposure.financialPositions * price,
      futureExpCalendar: futureExpCalendar || [], // we need the material exposure, no need for the price
      positionExpCalendar: positionExpCalendar || [],
    };
    return acc;
  }, {} as Record<string, IMaterialExposure>);

  return {
    ...netExposure,
    materialsExposure: adjustedMaterialsExposure,
  };
};

export const isSameMonth = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

export const calcExposure = (netExposure: INetExposure, materialId: string): number => {
  const materialExp = netExposure?.materialsExposure[materialId] || {
    futureOrder: 0,
    inventorySum: 0,
    financialPositions: 0,
  };
  return materialExp.futureOrder - materialExp.inventorySum - materialExp.financialPositions;
};

export interface IExposureHistory {
  key: string;
  accountId: string;
  materialId: string;
  createDate: Timestamp;
  financialPosition: number;
  inventory: number;
  futureOrders: number;
}

const dateStr = (date: Date) => date.toISOString().split('T')[0];
// Helper function to generate all relevant time points for a specific material
const generateTimePointsForMaterial = (
  futureExposures: FutureOrderExposure[],
  inventoryReports: IInventoryReportType[],
  financialPositions: IFinancialPosition[],
  materialId: string,
): Date[] => {
  return Array.from(
    new Set<string>([
      ...(futureExposures
        ?.filter((exposure) => exposure.materialId === materialId)
        ?.map(({ exposureDate }) => dateStr(exposureDate.toDate())) || []),
      ...(inventoryReports
        ?.filter((report) => report.materialId === materialId)
        ?.map(({ reportingTime }) => dateStr(reportingTime.toDate())) || []),
      ...(financialPositions
        ?.filter((position) => position.materialId === materialId)
        ?.map(({ createDate }) => dateStr(createDate.toDate())) || []),
    ]),
  )
    .map((dateStr) => new Date(dateStr))
    .sort((a, b) => a.getTime() - b.getTime());
};

// Helper function to get all unique material IDs from the data
const getAllMaterialIds = (
  futureExposures: FutureOrderExposure[],
  inventoryReports: IInventoryReportType[],
  financialPositions: IFinancialPosition[],
): string[] =>
  Array.from(
    new Set<string>([
      ...(futureExposures?.map(({ materialId }) => materialId) || []),
      ...(inventoryReports?.map(({ materialId }) => materialId) || []),
      ...(financialPositions?.map(({ materialId }) => materialId) || []),
    ]),
  );

/**
 * Fetches all data for an account and calculates customer exposure history per material
 * Returns a record with keys in format "ACCOUNT__MATERIALID__DATE" and exposure history values
 */
export const calcAccountExposeHistory = async (
  accountId: string,
  logText: (_text: string) => void,
): Promise<Record<string, IExposureHistory>> => {
  try {
    // Fetch all data in parallel
    const [futureExposures, inventoryReports, financialPositions] = await Promise.all([
      getFutureExposure(accountId),
      getAllInventoryReportOfAccount(accountId, ['completed']),
      getPositions(accountId),
    ]);

    if (!futureExposures && !inventoryReports && !financialPositions) {
      return {};
    }
    logText(
      `Fetched FutExp: ${futureExposures?.length || 0}, Inv: ${inventoryReports?.length || 0}, FinPos: ${
        financialPositions?.length || 0
      }`,
    );
    // Get all unique material IDs
    const materialIds = getAllMaterialIds(futureExposures || [], inventoryReports || [], financialPositions || []);

    const exposureHistory: Record<string, IExposureHistory> = {};

    // Calculate exposure history for each material separately
    materialIds.forEach((currMatId) => {
      // Generate time points for this specific material
      const materialTimePoints = generateTimePointsForMaterial(
        futureExposures || [],
        inventoryReports || [],
        financialPositions || [],
        currMatId,
      );

      materialTimePoints.forEach((timePoint) => {
        // Calculate inventory up to this time point for this material
        const inventoryAtTime = inventoryReports
          ?.filter(
            ({ materialId, reportingTime, dontSum }) =>
              materialId === currMatId && reportingTime.toDate() >= timePoint && !dontSum,
          )
          ?.reduce((acc, { quantity }) => acc + quantity, 0);

        // Calculate financial positions up to this time point for this material
        const financialPositionsAtTime = financialPositions
          ?.filter(({ materialId, createDate }) => materialId === currMatId && createDate.toDate() >= timePoint)
          ?.reduce(
            (acc, { routeQuantity, positionType }) => acc + routeQuantity * (positionType === 'long' ? 1 : -1),
            0,
          );

        // Calculate future order exposure for this specific time point and material
        const futureOrderExposure = futureExposures
          ?.filter(({ materialId, exposureDate }) => materialId === currMatId && exposureDate.toDate() >= timePoint)
          ?.reduce((acc, { exposureValue }) => acc + exposureValue, 0);

        // Generate key in format ACCOUNT__MATERIALID__DATE
        const key = `${accountId}__${currMatId}__${dateStr(timePoint)}`;

        // Create exposure history object for this material
        exposureHistory[key] = {
          key,
          createDate: Timestamp.fromDate(timePoint),
          financialPosition: financialPositionsAtTime || 0,
          inventory: inventoryAtTime || 0,
          futureOrders: futureOrderExposure || 0,
          materialId: currMatId,
          accountId,
        };
      });
    });
    logText(`Calculated exposure history entries: ${Object.keys(exposureHistory).length}`);
    return exposureHistory;
  } catch (error) {
    console.error('Error calculating customer exposure history:', error);
    throw error;
  }
};
